/**
 * Recevoir une trousse offerte (`/trousse/$token`, route PUBLIQUE, migration 0738) : on voit la trousse,
 * son numéro et qui l'offre ; connecté, « Prendre ma trousse » l'ajoute à sa collection. Sans compte :
 * connexion ou création sur place. Rien n'est payant : vaut aussi dans l'app native.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardDescription, CardTitle } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { TrousseShowcase } from '@/features/trousse/trousse-showcase';
import { useAuth } from '@/lib/auth';
import { setReturnTo } from '@/lib/return-to';
import {
  claimTrousseGift,
  myTrousseKey,
  normalizeTrousseToken,
  serialLabel,
  trousseCollectionKey,
  trousseGiftErrorMessage,
  trousseRarity,
  useEquipFromCollection,
  usePublicTrousseGift,
} from '@/lib/trousses';

const LINK_CLASS =
  'bg-primary-500 inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl px-5 text-sm font-semibold text-white active:scale-[0.98]';

export function TrousseGiftPage({ token: raw }: { token: string }) {
  const token = normalizeTrousseToken(raw);
  const { session, user } = useAuth();
  const qc = useQueryClient();
  const gift = usePublicTrousseGift(token, user?.id);
  const equip = useEquipFromCollection(user?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taken, setTaken] = useState(false);
  const g = gift.data;
  const anonymous = !!user?.is_anonymous;

  // un nouveau compte passe par l'inscription : il revient ensuite prendre sa trousse
  useEffect(() => {
    if (token && (!session || anonymous)) setReturnTo(`/trousse/${token}`);
  }, [token, session, anonymous]);

  async function take() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await claimTrousseGift(token);
      setTaken(true);
      if (user) {
        void qc.invalidateQueries({ queryKey: trousseCollectionKey(user.id) });
        void qc.invalidateQueries({ queryKey: myTrousseKey(user.id) });
      }
      void qc.invalidateQueries({ queryKey: ['trousse-gift', token] });
    } catch (e) {
      setError(trousseGiftErrorMessage((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  const mine = taken || !!g?.mine;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-8">
      <h1 className="font-display text-ink-900 mb-5 text-center text-2xl font-extrabold">
        Une trousse pour toi !
      </h1>

      <Card className="border-ink-100/60 space-y-4 p-5 shadow-lg">
        {!token || g?.status === 'not_found' ? (
          <p role="alert" className="text-error-600 text-sm">
            {trousseGiftErrorMessage('gift_not_found')}
          </p>
        ) : gift.isLoading || !g ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-xl" />
        ) : (
          <>
            <div className="text-center">
              {g.image_path && (
                <TrousseShowcase
                  key={mine ? 'mine' : 'preview'}
                  trousse={{
                    slug: g.slug,
                    rank: g.rank ?? 0,
                    copies: g.copies ?? 1,
                    image_path: g.image_path,
                  }}
                  celebrate={taken}
                />
              )}
              <p className="text-ink-800 mt-3">
                {g.from_first_name ? `${g.from_first_name} t’offre ` : 'On t’offre '}
                la trousse <strong>« {g.name} »</strong>.
              </p>
              {g.copies != null && g.serial != null && (
                <p className="text-ink-500 mt-1 text-xs">
                  <span className="capitalize">{trousseRarity(g.copies)}</span> ·{' '}
                  {serialLabel(g.serial, g.copies)}
                </p>
              )}
            </div>

            {mine ? (
              <div className="space-y-2 text-center">
                <p className="font-semibold text-green-700">Elle est dans ta collection !</p>
                <Button
                  fullWidth
                  loading={equip.isPending}
                  disabled={equip.isSuccess}
                  onClick={() => g.slug && equip.mutate(g.slug)}
                >
                  {equip.isSuccess ? 'Tu la portes' : 'La porter'}
                </Button>
                <Link to="/trousses" className={LINK_CLASS}>
                  Voir ma collection
                </Link>
              </div>
            ) : g.status === 'claimed' ? (
              <p className="text-ink-600 text-center text-sm">
                {trousseGiftErrorMessage('gift_already_claimed')}
              </p>
            ) : g.own_gift ? (
              <div className="space-y-2 text-center">
                <p className="text-ink-600 text-sm">{trousseGiftErrorMessage('own_gift')}</p>
                <Link to="/trousses" className={LINK_CLASS}>
                  Ma collection
                </Link>
              </div>
            ) : !session || anonymous ? (
              <OtpAuthForm
                initialMode="signup"
                renderHeader={() => (
                  <>
                    <CardTitle>Connecte-toi pour la prendre</CardTitle>
                    <CardDescription>
                      Crée ton compte ou connecte-toi : la trousse rejoint ensuite ta collection.
                    </CardDescription>
                  </>
                )}
              />
            ) : (
              <div className="space-y-2">
                {!!g.owned_count && (
                  <p className="text-ink-600 text-center text-sm">
                    Tu en as déjà {g.owned_count} : celle-ci s’ajoute à ta collection, avec son
                    numéro.
                  </p>
                )}
                <Button fullWidth size="lg" loading={busy} onClick={() => void take()}>
                  Prendre ma trousse
                </Button>
              </div>
            )}
            {error && (
              <p role="alert" className="text-error-600 text-center text-sm">
                {error}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
